import {useState, useEffect} from 'react';
import Drawer from '@mui/material/Drawer';
import Close from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import ConfirmDownloadForm from '@IssueConsoleForms/ConfirmDownloadForm/index.js';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import TicketExportJobRow from '@IssueConsoleRepresentationalDisplay/TicketExportJobRow';
import TicketExportJobService from '@Helpers/URLHelpers/TicketExportJobService';
import ErrorHandler from '@Root/ErrorHandler';
import './TicketExportJob.scss';

export default function TicketExportJob(props){

    const [cursor] = useState('auto');
    const [ticketExportCount, setTicketExportCount] = useState(0);
    const [isExporting, setIsExporting] = useState(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [notifyDownload, setNotifyDownload] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [ticketExportJobList, setTicketExportJobList] = useState([]);
    const [showConfirmDownloadForm, setShowConfirmDownloadForm] = useState(false);
    const [kaiziqResolveConnectionId, setKaiziqResolveConnectionId] = useState(0);
    const [snackBarOpen, setSnackBarOpen] = useState(false);
    const [snackBarType, setSnackBarType] = useState('success');
    const [snackBarMessage, setSnackBarMessage] = useState('');
    const { currentProjectId } = useReduxProject();
    const ticketExportJobService = new TicketExportJobService();
    const errorHandler = new ErrorHandler();
    const className = 'ticketExportJob';

    useEffect(()=>{
        renderData();
    }, [props.filters])

    let interval;

    useEffect(()=>{
        interval = setInterval(() => {
             fetchRunningInstanceCount();
        }, 3000);
        return () => {
            clearInterval(interval);
        }
    },[ticketExportCount, isExporting])

    const fetchRunningInstanceCount = async()=> {
        try {
            if(ticketExportCount !== 0) {
                let response = await ticketExportJobService.getRunningInstanceCount(currentProjectId, props.filters.ticketFilter?.kaiziqResolveConnectionIdList[0]);
                if(response.status < 400) {
                    let count = parseInt(response.body);
                    setTicketExportCount(count);
                }
            } else {
                if(isExporting) {
                    setIsExporting(false);
                    setNotifyDownload(true);
                    const failed = await didLatestExportFail();
                    if(failed) {
                        setSnackBarMessage(failed);
                        setSnackBarType('error');
                    } else {
                        setSnackBarMessage('Ticket Exported...');
                        setSnackBarType('success');
                    }
                    setSnackBarOpen(true);
                }
            }
        } catch(err) {
            errorHandler.handleError(err);
        }
    }

    const didLatestExportFail = async () => {
        try {
            const connectionId = kaiziqResolveConnectionId || props.filters.ticketFilter?.kaiziqResolveConnectionIdList?.[0];
            const response = await ticketExportJobService.getListByProjectIdAndConnectionId(currentProjectId, connectionId);
            if(response.status >= 400) {
                return '';
            }
            const jobs = JSON.parse(response.body);
            const latest = Array.isArray(jobs) ? jobs[0] : null;
            if(latest?.conclusion !== 'failed') {
                return '';
            }
            let reason = '';
            try {
                reason = latest?.details ? (JSON.parse(latest.details)?.error || '') : '';
            } catch (parseErr) {
                reason = '';
            }
            return reason ? `Ticket export failed: ${reason}` : 'Ticket export failed. Please try again.';
        } catch (err) {
            return '';
        }
    };

    const renderData = async ()=> {
        let resolveConnectionId = props.filters.ticketFilter?.kaiziqResolveConnectionIdList[0];
        setKaiziqResolveConnectionId(resolveConnectionId);
    }

    const handleDrawer = ()=>{
        setOpenDrawer(!openDrawer);
    }

    const renderTicketExportJobList = async()=> {
        try {
            setIsLoading(true);
            let response = await ticketExportJobService.getListByProjectIdAndConnectionId(currentProjectId, kaiziqResolveConnectionId);
            if(response.status < 400) {
                response = JSON.parse(response.body);
                setTicketExportJobList(response);
            }
        }catch(err) {
            errorHandler.handleError(err);
        }
        setIsLoading(false);
    }

    const handleExport = async (e) => {
        e?.preventDefault();
        setShowConfirmDownloadForm((oldValue)=>(!oldValue));
    }

    const toggleConfirmDownloadForm = () => {
        setShowConfirmDownloadForm(!showConfirmDownloadForm);
    }

    const handleHistoryButtonClick = async ()=> {
        setNotifyDownload(false);
        renderTicketExportJobList();
        handleDrawer();
    }

    const handleSnackBarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackBarOpen(false);
    };

    return (
        <div
            className={className}
            style={{cursor : cursor}}>
                <div className={className + '__top flex-row-center-1vw-gap'}>
                    {
                        isExporting ?
                            <CircularProgress className={className + '__top__downloading'} />
                        :
                            props.iconOnly ?
                                <IconButton
                                    size='small'
                                    onClick={handleExport}
                                    aria-label='Export tickets'
                                    className={props.iconButtonClassName || className + '__top__iconButton'}>
                                    <FileDownloadOutlinedIcon fontSize='small' />
                                </IconButton>
                            :
                                <BorderBlueButtonV2
                                    text='Export Tickets'
                                    onClick={handleExport}
                                    className={props.exportButtonClassName || className + '__top__exportButton'} />
                    }
                    <div className={className + '__top__history'}>
                        {
                            props.iconOnly ?
                                <IconButton
                                    size='small'
                                    onClick={handleHistoryButtonClick}
                                    aria-label='Export history'
                                    className={props.iconButtonClassName || className + '__top__iconButton'}>
                                    <HistoryIcon fontSize='small' />
                                </IconButton>
                            :
                                <HistoryIcon className={className + '__top__history__icon standard-link'} onClick={handleHistoryButtonClick}/>
                        }
                        {
                            notifyDownload &&
                                <div className={className + '__top__history__dot'}/>
                        }
                    </div>
                </div>
                <Drawer
                    size={'lg'}
                    anchor='right'
                    onClose={handleDrawer}
                    open={openDrawer}
                    className={className + '__body'}>
                    <div className={className + '__body__inner'}>
                        <div className={className + '__body__inner__top'}>
                            <div className={className + '__body__inner__top__left'}>
                                <div className={className + '__body__inner__top__left__title'}>
                                    Ticket Export History
                                </div>
                                <div className={className + '__body__inner__top__left__subtitle'}>
                                    {ticketExportJobList.length} export {ticketExportJobList.length === 1 ? 'job' : 'jobs'}
                                </div>
                            </div>
                            <IconButton
                                size='small'
                                className={className + '__body__inner__top__close'}
                                onClick={handleDrawer}
                                aria-label='Close history panel'>
                                <Close />
                            </IconButton>
                        </div>
                        <div className={className + '__body__inner__bottom'}>
                            {
                                isLoading ?
                                    <Box className={className + '__body__inner__bottom__loading'}>
                                        <CircularProgress />
                                        <Typography className={className + '__body__inner__bottom__loading__text'}>
                                            Loading export history...
                                        </Typography>
                                    </Box>
                                :
                                    <div className={className + '__body__inner__bottom__item'}>
                                        {
                                            ticketExportJobList.length === 0 ?
                                                <div className={className + '__body__inner__bottom__item__noItem'}>
                                                    <HistoryIcon className={className + '__body__inner__bottom__item__noItem__icon'} />
                                                    <div className={className + '__body__inner__bottom__item__noItem__title'}>
                                                        No exports yet
                                                    </div>
                                                    <div className={className + '__body__inner__bottom__item__noItem__subtitle'}>
                                                        Start a download and your export jobs will appear here.
                                                    </div>
                                                </div>
                                            :
                                                ticketExportJobList.map((item, index)=>
                                                    <TicketExportJobRow
                                                        key={item.id}
                                                        item={item}
                                                        index={index + 1} />
                                                )
                                        }
                                    </div>
                            }
                        </div>
                    </div>
                </Drawer>
                {
                    showConfirmDownloadForm &&
                        <ConfirmDownloadForm
                            handleExport={handleExport}
                            kaiziqResolveConnectionId={kaiziqResolveConnectionId}
                            open={showConfirmDownloadForm}
                            closePopup={toggleConfirmDownloadForm}
                            filters={props.filters}/>
                }
                <Snackbar
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }}
                    open={snackBarOpen}
                    autoHideDuration={5000}
                    onClose={handleSnackBarClose}>
                    <Alert
                        onClose={handleSnackBarClose}
                        severity={snackBarType}
                        variant='filled'
                        sx={{ width: '100%' }}>
                        {snackBarMessage}
                    </Alert>
                </Snackbar>
        </div>
    )
}